import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ActionIcon,
  Button,
  Center,
  Container,
  Divider,
  Group,
  List,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
  Textarea,
  Select,
} from '@mantine/core'
import { IconClipboardList, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import type { BoardDto, TaskPriority } from '@ledokol/contracts'
import { deleteProject, fetchBoard } from '../shared/api/projects'
import { createTask, transitionTask } from '../shared/api/tasks'
import { AppLayout } from '../features/projects/app-shell'
import { KanbanBoard } from '../features/kanban/board'
import { TaskModal } from '../features/task-modal/task-modal'
import { useAuthStore } from '../features/auth/store'
import { isApiError } from '../shared/api/client'

export function ProjectBoardPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)
  const [createOpened, setCreateOpened] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const canDelete = user?.role === 'admin' || user?.role === 'director'

  const deleteMut = useMutation({
    mutationFn: () => deleteProject(id!),
    onSuccess: () => {
      notifications.show({ message: `Проект «${boardQ.data?.project.name}» удалён`, color: 'green' })
      qc.invalidateQueries({ queryKey: ['projects'] })
      navigate('/projects', { replace: true })
    },
    onError: (e: unknown) => {
      const msg = isApiError(e) ? e.message : 'Не удалось удалить'
      notifications.show({ message: msg, color: 'red' })
    },
  })

  const boardQ = useQuery({
    queryKey: ['board', id, search || null],
    queryFn: () => fetchBoard(id!, search || undefined),
    enabled: !!id,
  })

  const moveMut = useMutation({
    mutationFn: ({ taskId, toColumnId }: { taskId: string; toColumnId: string }) =>
      transitionTask(taskId, { to_column_id: toColumnId }),
    onMutate: async ({ taskId, toColumnId }) => {
      // Optimistic: переносим карточку в локальном кеше
      await qc.cancelQueries({ queryKey: ['board', id] })
      const prev = qc.getQueryData<BoardDto>(['board', id, search || null])
      if (!prev) return { prev: null }
      const next: BoardDto = {
        ...prev,
        columns: prev.columns.map((c) => ({ ...c, tasks: c.tasks.filter((t) => t.id !== taskId) })),
      }
      const card = prev.columns.flatMap((c) => c.tasks).find((t) => t.id === taskId)
      if (card) {
        next.columns = next.columns.map((c) =>
          c.id === toColumnId ? { ...c, tasks: [...c.tasks, card] } : c,
        )
      }
      qc.setQueryData(['board', id, search || null], next)
      return { prev }
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['board', id, search || null], ctx.prev)
      let msg = 'Не удалось переместить задачу'
      if (isApiError(err)) {
        const e = err.response?.data?.error
        if (e?.rule === 'employee_adjacent_columns_only') {
          msg = 'Сотрудник может двигать карточку только в соседнюю колонку'
        } else if (e?.code === 'forbidden') {
          msg = 'Нет прав двигать эту задачу'
        }
      }
      notifications.show({ color: 'red', message: msg })
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['board', id] })
    },
  })

  return (
    <AppLayout>
      <Container size="xl" py="md">
        <Group justify="space-between" mb="md">
          <div>
            <Text size="xs" c="blue.7" fw={700} tt="uppercase">
              Канбан-доска
            </Text>
            <Title order={2}>{boardQ.data?.project.name ?? '…'}</Title>
          </div>
          <Group>
            <TextInput
              placeholder="Поиск по заголовку…"
              leftSection={<IconSearch size={14} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={260}
            />
            <Button
              component={Link}
              to={`/projects/${id}/details`}
              variant="default"
              leftSection={<IconClipboardList size={16} />}
            >
              Детали
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpened(true)}>
              Новая задача
            </Button>
            {canDelete && (
              <ActionIcon
                variant="subtle"
                color="red"
                size="lg"
                onClick={() => setConfirmDelete(true)}
                title="Удалить проект"
              >
                <IconTrash size={18} />
              </ActionIcon>
            )}
          </Group>
        </Group>

        {boardQ.isLoading && (
          <Center mih={400}>
            <Loader />
          </Center>
        )}

        {boardQ.data && (
          <KanbanBoard
            board={boardQ.data}
            onTaskMove={(taskId, toColumnId) => moveMut.mutate({ taskId, toColumnId })}
            onTaskClick={(taskId) => setOpenTaskId(taskId)}
          />
        )}
      </Container>

      <TaskModal taskId={openTaskId} onClose={() => setOpenTaskId(null)} onOpenTask={setOpenTaskId} />

      <Modal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={`Удалить проект «${boardQ.data?.project.name ?? ''}»?`}
        centered
        size="md"
      >
        <Stack>
          <Text size="sm">Что произойдёт:</Text>
          <List size="sm" spacing="xs">
            <List.Item>Проект исчезнет из списка и фильтров.</List.Item>
            <List.Item>
              <Text component="span" size="sm">
                Задачи проекта <Text component="span" fw={600}>сохранятся</Text> в БД (FK не теряем) —
                в KPI/отчётах продолжат учитываться.
              </Text>
            </List.Item>
            <List.Item>
              Если к проекту привязаны Telegram-чаты — привязки обрываются, бот в этих чатах увидит «чат не привязан».
            </List.Item>
            <List.Item>Восстановить можно только через БД (admin SQL).</List.Item>
          </List>
          <Divider my="xs" />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setConfirmDelete(false)} disabled={deleteMut.isPending}>
              Отмена
            </Button>
            <Button color="red" loading={deleteMut.isPending} onClick={() => deleteMut.mutate()}>
              Удалить
            </Button>
          </Group>
        </Stack>
      </Modal>

      {boardQ.data && (
        <NewTaskModal
          opened={createOpened}
          onClose={() => setCreateOpened(false)}
          projectId={id!}
        />
      )}
    </AppLayout>
  )
}

function NewTaskModal({
  opened,
  onClose,
  projectId,
}: {
  opened: boolean
  onClose: () => void
  projectId: string
}) {
  const qc = useQueryClient()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<TaskPriority>('med')

  const mut = useMutation({
    mutationFn: () =>
      createTask(projectId, {
        title: title.trim(),
        description: description.trim() || null,
        priority,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['board', projectId] })
      setTitle('')
      setDescription('')
      setPriority('med')
      onClose()
      notifications.show({ color: 'green', message: 'Задача создана' })
    },
    onError: (e) => {
      let msg = 'Не удалось создать задачу'
      if (isApiError(e)) {
        msg = e.response?.data?.error?.message ?? msg
      }
      notifications.show({ color: 'red', message: msg })
    },
  })

  // Reset on close
  useEffect(() => {
    if (!opened) {
      setTitle('')
      setDescription('')
      setPriority('med')
    }
  }, [opened])

  return (
    <Modal opened={opened} onClose={onClose} title="Новая задача" size="md">
      <Stack>
        <TextInput
          label="Заголовок"
          required
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          maxLength={500}
        />
        <Textarea
          label="Описание"
          autosize
          minRows={3}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <Select
          label="Приоритет"
          value={priority}
          onChange={(v) => v && setPriority(v as TaskPriority)}
          data={[
            { value: 'low', label: 'Низкий' },
            { value: 'med', label: 'Средний' },
            { value: 'high', label: 'Высокий' },
            { value: 'urgent', label: 'Срочный' },
          ]}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={() => mut.mutate()} loading={mut.isPending} disabled={!title.trim()}>
            Создать
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
